import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import omit from 'lodash/omit';
import reject from 'lodash/reject';

import { HighlightActions } from 'actions/actionTypes';

export type TMarkerRequestReason =
  | 'playback_mount'
  | 'legacy_recording_mount'
  | 'snippets_mount'
  | 'snippets_sort'
  | 'activity_mount'
  | 'manual_refresh';

export type TMarkerRequestConsumer = 'background' | 'foreground';
export type TMarkerRequestStatus =
  | 'idle'
  | 'loading'
  | 'succeeded'
  | 'stale'
  | 'failed';
export type TMarkerEvaluationOutcome =
  | 'started'
  | 'joined'
  | 'hydrated'
  | 'zero_skipped';

interface IPlaylist extends Record<string, unknown> {
  uuid: string;
  name?: string;
  privacy?: string;
}

export interface IHighlight extends Record<string, unknown> {
  uuid: string;
  meeting: string;
  playlists?: IPlaylist[];
}

interface IHighlightCategory extends Record<string, unknown> {
  uuid: string;
  visible?: boolean;
}

interface IHighlightCategoryOverride extends Record<string, unknown> {
  uuid: string;
  visible: boolean;
  category: IHighlightCategory;
}

interface IAsyncOperationState {
  loading: boolean;
  error?: boolean;
}

interface IHighlightSharesState extends IAsyncOperationState {
  data: Record<string, unknown> | null;
  step?: unknown;
  shareMethod?: unknown;
  highlightUuids?: string[];
}

interface IHighlightRecordingState extends Record<string, unknown> {
  loading: boolean;
}

export interface IMarkerRequestMetadata {
  requestId: string;
  meetingUuid: string;
  orderBy?: string;
  status: TMarkerRequestStatus;
  error: unknown | null;
  reason: TMarkerRequestReason;
  consumer: TMarkerRequestConsumer;
  affectsSnippetsLoading: boolean;
  entryToken?: string;
  hostInstanceId?: string;
  entityRevisionAtStart: number;
  requestedExpectedCount?: number;
  maxQueuedExpectedCount?: number;
  attempt: 1 | 2;
  evaluationOutcome?: TMarkerEvaluationOutcome;
  joined: boolean;
  promoted: boolean;
  revalidation: boolean;
  countFollowup: boolean;
  superseded: boolean;
  needsRevalidation: boolean;
  lastSucceededAt?: number;
}

export interface IBackgroundMarkerAttempt {
  meetingUuid: string;
  orderBy?: string;
  entryToken: string;
  attemptedExpectedCount?: number;
  evaluationOutcome: TMarkerEvaluationOutcome;
}

interface ISnippetsLoadingOwner {
  meetingUuid: string;
  orderBy: string;
}

export interface IHighlightsState {
  data: Record<string, IHighlight[]>;
  fetching: boolean;
  creating: boolean;
  error: unknown | null;
  highlightCategoryOverrides: IHighlightCategoryOverride[];
  highlightCategoriesForUser: IHighlightCategory[];
  highlightCategoriesUserMap: Record<string, IHighlightCategory>;
  highlightRecordings: Record<string, IHighlightRecordingState>;
  fetchingCategoryOverrides: boolean;
  shares: IHighlightSharesState;
  shareHighlight: IAsyncOperationState;
  unshareHighlight: IAsyncOperationState;
  updateHighlightShares: IAsyncOperationState;
  updateHighlight: IAsyncOperationState;
  updatingHighlight?: IAsyncOperationState;
  updatingHighlightPrivacy?: IAsyncOperationState;
  deleteHighlight: IAsyncOperationState;
  downloadHighlight: IAsyncOperationState;
  addHighlightToPlaylist: IAsyncOperationState;
  removeHighlightFromPlaylist: IAsyncOperationState;
  orderedBy: string;
  requestsByMeeting: Record<string, Record<string, IMarkerRequestMetadata>>;
  entityRevisionByMeeting: Record<string, number>;
  latestEntityRequestIdByMeeting: Record<string, string>;
  selectedOrderByMeeting: Record<string, string>;
  dataOrderByMeeting: Record<string, string>;
  lastBackgroundAttemptByHostInstance: Record<string, IBackgroundMarkerAttempt>;
  snippetsLoadingRequestsById: Record<string, ISnippetsLoadingOwner>;
}

interface IHighlightAction {
  type: string;
  requestId?: string;
  meetingUuid?: string;
  orderedBy?: string;
  orderBy?: string;
  reason?: TMarkerRequestReason;
  consumer?: TMarkerRequestConsumer;
  affectsSnippetsLoading?: boolean;
  entryToken?: string;
  hostInstanceId?: string;
  expectedCount?: number;
  requestedExpectedCount?: number;
  maxQueuedExpectedCount?: number;
  entityRevisionAtStart?: number;
  attempt?: number;
  evaluationOutcome?: TMarkerEvaluationOutcome;
  evaluationOnly?: boolean;
  releaseHost?: boolean;
  joined?: boolean;
  promoted?: boolean;
  revalidation?: boolean;
  countFollowup?: boolean;
  applyEntities?: boolean;
  superseded?: boolean;
  stale?: boolean;
  needsRevalidation?: boolean;
  error?: unknown;
  highlights?: IHighlight[];
  newHighlight?: IHighlight;
  highlightUuid?: string;
  highlightUuids?: string[];
  response?: Record<string, unknown>;
  privacy?: unknown;
  privacySettings?: Record<string, unknown>;
  playlist?: IPlaylist;
  playlistContentUuid?: string;
  playlistUuid?: string;
  uuid?: string;
  highlightCategoryOverrides?: IHighlightCategoryOverride[];
  highlightRecording?: IHighlightRecordingState & { highlight: string };
  step?: unknown;
  shareMethod?: unknown;
  shares?: Record<string, unknown>;
  highlightObj?: IHighlight;
}

export const highlightsInitialState: IHighlightsState = {
  data: {}, // keyed by meeting id?
  fetching: false,
  creating: false,
  error: null,
  highlightCategoryOverrides: [],
  highlightCategoriesForUser: [],
  highlightCategoriesUserMap: {},
  highlightRecordings: {},
  fetchingCategoryOverrides: false,
  shares: {
    loading: false,
    error: false,
    data: null
  },
  shareHighlight: {
    loading: false,
    error: false
  },
  unshareHighlight: {
    loading: false,
    error: false
  },
  updateHighlightShares: {
    loading: false,
    error: false
  },
  updateHighlight: {
    loading: false,
    error: false
  },
  deleteHighlight: {
    loading: false
  },
  downloadHighlight: {
    loading: false,
    error: false
  },
  addHighlightToPlaylist: {
    loading: false,
    error: false
  },
  removeHighlightFromPlaylist: {
    loading: false,
    error: false
  },
  orderedBy: 'created',
  requestsByMeeting: {},
  entityRevisionByMeeting: {},
  latestEntityRequestIdByMeeting: {},
  selectedOrderByMeeting: {},
  dataOrderByMeeting: {},
  lastBackgroundAttemptByHostInstance: {},
  snippetsLoadingRequestsById: {}
};

const arrToMap = (highlightArr: IHighlightCategory[]) => {
  const highlightMap: Record<string, IHighlightCategory> = {};
  highlightArr.forEach(hh => {
    highlightMap[hh.uuid] = hh;
  });
  return highlightMap;
};

const getNextEntityRevision = (state: IHighlightsState, meetingUuid: string) =>
  (state.entityRevisionByMeeting?.[meetingUuid] || 0) + 1;

const getRequestOrder = (action: IHighlightAction) =>
  action.orderBy || action.orderedBy || 'created';

const getRequestReason = (action: IHighlightAction): TMarkerRequestReason => {
  if (action.reason) return action.reason;
  return getRequestOrder(action) === 'created'
    ? 'snippets_mount'
    : 'snippets_sort';
};

const isOrderOwningRequest = (action: IHighlightAction) => {
  const reason = getRequestReason(action);
  return reason === 'snippets_mount' || reason === 'snippets_sort';
};

const getRequestAttempt = (action: IHighlightAction): 1 | 2 =>
  action.attempt === 2 ? 2 : 1;

const addSnippetsLoadingOwner = (
  state: IHighlightsState,
  action: IHighlightAction,
  orderBy: string
) => {
  const existingLoadingOwners = state.snippetsLoadingRequestsById || {};
  if (!action.requestId || action.affectsSnippetsLoading !== true) {
    return existingLoadingOwners;
  }

  return {
    ...existingLoadingOwners,
    [action.requestId]: {
      meetingUuid: action.meetingUuid || '',
      orderBy
    }
  };
};

const removeSnippetsLoadingOwner = (
  state: IHighlightsState,
  requestId?: string
) => {
  const existingLoadingOwners = state.snippetsLoadingRequestsById || {};
  if (!requestId || !existingLoadingOwners[requestId]) {
    return existingLoadingOwners;
  }

  return omit(existingLoadingOwners, [requestId]);
};

const getFetchingFromOwners = (
  loadingOwners: Record<string, ISnippetsLoadingOwner>
) => Object.keys(loadingOwners).length > 0;

const creatingHighlight = (
  state: IHighlightsState,
  action: IHighlightAction
): IHighlightsState => {
  switch (action.type) {
    case HighlightActions.CREATE_HIGHLIGHT_REQUEST:
      return { ...state, creating: true, error: null };
    case HighlightActions.CREATE_HIGHLIGHT_SUCCESS: {
      if (!action.newHighlight) return state;
      const { data } = state;
      const highlightList = [...(data[action.newHighlight.meeting] || [])];
      highlightList.push(action.newHighlight);
      return {
        ...state,
        data: {
          ...state.data,
          [action.newHighlight.meeting]: highlightList
        },
        creating: false,
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [action.newHighlight.meeting]: getNextEntityRevision(
            state,
            action.newHighlight.meeting
          )
        }
      };
    }
    case HighlightActions.CREATE_HIGHLIGHT_FAILURE:
      return {
        ...state,
        creating: false,
        error: action.error
      };
    default:
      return state;
  }
};

const updatingHighlight = (
  state: IHighlightsState,
  action: IHighlightAction
): IHighlightsState => {
  switch (action.type) {
    case HighlightActions.UPDATE_HIGHLIGHT_REQUEST:
      return {
        ...state,
        updatingHighlight: {
          loading: true
        }
      };
    case HighlightActions.UPDATE_HIGHLIGHT_PRIVACY_REQUEST:
      return {
        ...state,
        updatingHighlightPrivacy: {
          loading: true
        }
      };
    case HighlightActions.UPDATE_HIGHLIGHT_SUCCESS: {
      const { meetingUuid, highlightUuid } = action;
      if (!meetingUuid || !highlightUuid) return state;
      const meetingHighlights = state.data[meetingUuid] || [];
      const updatedHighlights = meetingHighlights.map(highlight => {
        if (highlight.uuid === highlightUuid) {
          return {
            ...highlight,
            ...(action.response || {})
          };
        }
        return highlight;
      });
      return {
        ...state,
        data: {
          ...state.data,
          [meetingUuid]: updatedHighlights
        },
        updatingHighlight: {
          loading: false
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [meetingUuid]: getNextEntityRevision(state, meetingUuid)
        }
      };
    }
    case HighlightActions.UPDATE_HIGHLIGHT_PRIVACY_SUCCESS: {
      const { meetingUuid, highlightUuid, privacy, privacySettings } = action;
      if (!meetingUuid || !highlightUuid) return state;
      const meetingHighlights = state.data[meetingUuid] || [];
      const updatedHighlights = meetingHighlights.map(highlight => {
        if (highlight.uuid === highlightUuid) {
          return {
            ...highlight,
            privacy,
            privacySettings: { ...(privacySettings || {}) }
          };
        }
        return highlight;
      });
      return {
        ...state,
        data: {
          ...state.data,
          [meetingUuid]: updatedHighlights
        },
        updatingHighlightPrivacy: {
          loading: false
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [meetingUuid]: getNextEntityRevision(state, meetingUuid)
        }
      };
    }
    case HighlightActions.UPDATE_HIGHLIGHT_FAILURE:
      return {
        ...state,
        updatingHighlight: {
          loading: false,
          error: true
        }
      };
    case HighlightActions.UPDATE_HIGHLIGHT_PRIVACY_FAILURE:
      return {
        ...state,
        updatingHighlightPrivacy: {
          loading: false,
          error: true
        }
      };
    case HighlightActions.UPDATE_HIGHLIGHTS_SUCCESS: {
      const { meetingUuid, highlightUuids } = action;
      if (!meetingUuid || !highlightUuids) return state;
      const meetingHighlights = state.data[meetingUuid] || [];
      const updatedHighlights = meetingHighlights.map(highlight => {
        if (highlightUuids.includes(highlight.uuid)) {
          return {
            ...highlight,
            ...(action.response || {})
          };
        }
        return highlight;
      });
      return {
        ...state,
        data: {
          ...state.data,
          [meetingUuid]: updatedHighlights
        },
        updatingHighlight: {
          loading: false
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [meetingUuid]: getNextEntityRevision(state, meetingUuid)
        }
      };
    }
    case HighlightActions.ADD_HIGHLIGHT_TO_PLAYLIST_REQUEST:
      return {
        ...state,
        addHighlightToPlaylist: {
          loading: true
        }
      };
    case HighlightActions.ADD_HIGHLIGHT_TO_PLAYLIST_SUCCESS: {
      const { meetingUuid, highlightUuid, playlist, playlistContentUuid } =
        action;

      if (!playlist?.uuid) {
        return state;
      }

      const playlistObj = {
        uuid: playlist.uuid,
        name: playlist.name,
        privacy: playlist.privacy,
        playlistContentUuid
      };

      if (!meetingUuid || !highlightUuid) return state;
      const meetingHighlights = state.data[meetingUuid] || [];
      const updatedHighlights = meetingHighlights.map(highlight => {
        if (highlight.uuid === highlightUuid) {
          return {
            ...highlight,
            playlists: [...(highlight.playlists || []), playlistObj]
          };
        }
        return highlight;
      });

      return {
        ...state,
        data: {
          ...state.data,
          [meetingUuid]: updatedHighlights
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [meetingUuid]: getNextEntityRevision(state, meetingUuid)
        }
      };
    }
    case HighlightActions.ADD_HIGHLIGHT_TO_PLAYLIST_FAILURE:
      return {
        ...state,
        addHighlightToPlaylist: {
          loading: false,
          error: true
        }
      };
    case HighlightActions.REMOVE_HIGHLIGHT_FROM_PLAYLIST_REQUEST:
      return {
        ...state,
        removeHighlightFromPlaylist: {
          loading: true
        }
      };
    case HighlightActions.REMOVE_HIGHLIGHT_FROM_PLAYLIST_SUCCESS: {
      const { meetingUuid, highlightUuid, playlistUuid } = action;
      if (!meetingUuid || !highlightUuid || !playlistUuid) return state;

      const meetingHighlights = state.data[meetingUuid] || [];

      const highlightIndex = meetingHighlights.findIndex(
        highlight => highlight.uuid === highlightUuid
      );

      if (highlightIndex === -1) return state;

      const updatedHighlights = meetingHighlights.map((highlight, index) =>
        index === highlightIndex
          ? {
              ...highlight,
              playlists: (highlight.playlists || []).filter(
                playlist => playlist.uuid !== playlistUuid
              )
            }
          : highlight
      );

      return {
        ...state,
        data: {
          ...state.data,
          [meetingUuid]: updatedHighlights
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [meetingUuid]: getNextEntityRevision(state, meetingUuid)
        }
      };
    }
    case HighlightActions.REMOVE_HIGHLIGHT_FROM_PLAYLIST_FAILURE:
      return {
        ...state,
        removeHighlightFromPlaylist: {
          loading: false,
          error: true
        }
      };

    default:
      return state;
  }
};

const deletingHighlight = (
  state: IHighlightsState,
  action: IHighlightAction
): IHighlightsState => {
  switch (action.type) {
    case HighlightActions.DELETE_HIGHLIGHT_REQUEST:
      return {
        ...state,
        deleteHighlight: {
          ...state.deleteHighlight,
          loading: true
        }
      };
    case HighlightActions.DELETE_HIGHLIGHT_SUCCESS: {
      if (!action.meetingUuid || !action.highlightUuid) return state;
      const { data } = state;
      return {
        ...state,
        data: {
          ...state.data,
          [action.meetingUuid]: reject(data[action.meetingUuid], {
            uuid: action.highlightUuid
          })
        },
        deleteHighlight: {
          ...state.deleteHighlight,
          loading: false
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [action.meetingUuid]: getNextEntityRevision(state, action.meetingUuid)
        }
      };
    }
    case HighlightActions.DELETE_HIGHLIGHT_FAILURE:
      return {
        ...state,
        deleteHighlight: {
          ...state.deleteHighlight,
          loading: false
        },
        error: action.error
      };
    default:
      return state;
  }
};

const recordBackgroundAttempt = (
  state: IHighlightsState,
  action: IHighlightAction,
  orderBy: string
) => {
  if (
    action.consumer !== 'background' ||
    !action.hostInstanceId ||
    !action.entryToken
  ) {
    return state.lastBackgroundAttemptByHostInstance || {};
  }

  return {
    ...(state.lastBackgroundAttemptByHostInstance || {}),
    [action.hostInstanceId]: {
      meetingUuid: action.meetingUuid || '',
      orderBy,
      entryToken: action.entryToken,
      attemptedExpectedCount:
        action.requestedExpectedCount ?? action.expectedCount,
      evaluationOutcome:
        action.evaluationOutcome || (action.joined ? 'joined' : 'started')
    }
  };
};

const createRequestMetadata = (
  state: IHighlightsState,
  action: IHighlightAction,
  orderBy: string,
  previous?: IMarkerRequestMetadata
): IMarkerRequestMetadata => {
  const requestedExpectedCount =
    previous?.requestedExpectedCount ??
    action.requestedExpectedCount ??
    action.expectedCount;
  const queuedExpectedCounts = [
    previous?.maxQueuedExpectedCount,
    action.maxQueuedExpectedCount,
    action.joined ? action.expectedCount : undefined
  ].filter((count): count is number => typeof count === 'number');

  return {
    requestId: action.requestId || previous?.requestId || '',
    meetingUuid: action.meetingUuid || previous?.meetingUuid || '',
    orderBy,
    status: 'loading',
    error: null,
    reason: action.reason || previous?.reason || getRequestReason(action),
    consumer:
      action.consumer ||
      (action.promoted ? 'foreground' : previous?.consumer) ||
      'foreground',
    affectsSnippetsLoading:
      previous?.affectsSnippetsLoading === true ||
      action.affectsSnippetsLoading === true,
    entryToken: action.entryToken || previous?.entryToken,
    hostInstanceId: action.hostInstanceId || previous?.hostInstanceId,
    entityRevisionAtStart:
      action.entityRevisionAtStart ??
      previous?.entityRevisionAtStart ??
      (action.meetingUuid
        ? state.entityRevisionByMeeting?.[action.meetingUuid] || 0
        : 0),
    requestedExpectedCount,
    maxQueuedExpectedCount:
      queuedExpectedCounts.length > 0
        ? Math.max(...queuedExpectedCounts)
        : previous?.maxQueuedExpectedCount,
    attempt: getRequestAttempt(action),
    evaluationOutcome: action.evaluationOutcome || previous?.evaluationOutcome,
    joined: previous?.joined === true || action.joined === true,
    promoted: previous?.promoted === true || action.promoted === true,
    revalidation: action.revalidation === true,
    countFollowup: action.countFollowup === true,
    superseded: false,
    needsRevalidation: action.revalidation
      ? previous?.needsRevalidation === true
      : false,
    lastSucceededAt: previous?.lastSucceededAt
  };
};

const setHighlightRequest = (
  state: IHighlightsState,
  meetingUuid: string,
  orderBy: string,
  request: IMarkerRequestMetadata
) => ({
  ...(state.requestsByMeeting || {}),
  [meetingUuid]: {
    ...(state.requestsByMeeting?.[meetingUuid] || {}),
    [orderBy]: request
  }
});

const fetchHighlights = (
  state: IHighlightsState,
  action: IHighlightAction
): IHighlightsState => {
  switch (action.type) {
    case HighlightActions.FETCH_HIGHLIGHTS_REQUEST: {
      if (
        action.evaluationOnly &&
        action.releaseHost &&
        action.hostInstanceId
      ) {
        return {
          ...state,
          lastBackgroundAttemptByHostInstance: omit(
            state.lastBackgroundAttemptByHostInstance || {},
            [action.hostInstanceId]
          )
        };
      }

      if (!action.requestId || !action.meetingUuid) {
        return {
          ...state,
          orderedBy: action.orderedBy || state.orderedBy,
          fetching: true
        };
      }

      const orderBy = getRequestOrder(action);
      const loadingOwners = addSnippetsLoadingOwner(state, action, orderBy);
      const lastBackgroundAttemptByHostInstance = recordBackgroundAttempt(
        state,
        action,
        orderBy
      );

      if (action.evaluationOnly) {
        const isOrderOwning = isOrderOwningRequest(action);
        return {
          ...state,
          orderedBy: isOrderOwning ? orderBy : state.orderedBy,
          selectedOrderByMeeting: isOrderOwning
            ? {
                ...state.selectedOrderByMeeting,
                [action.meetingUuid]: orderBy
              }
            : state.selectedOrderByMeeting,
          snippetsLoadingRequestsById: loadingOwners,
          fetching: getFetchingFromOwners(loadingOwners),
          lastBackgroundAttemptByHostInstance
        };
      }

      const previousRequest =
        state.requestsByMeeting?.[action.meetingUuid]?.[orderBy];
      const isSameLogicalRequest =
        previousRequest?.requestId === action.requestId;
      const request = createRequestMetadata(
        state,
        action,
        orderBy,
        isSameLogicalRequest ? previousRequest : undefined
      );
      const isOrderOwning = isOrderOwningRequest(action);

      return {
        ...state,
        orderedBy: isOrderOwning ? orderBy : state.orderedBy,
        fetching: getFetchingFromOwners(loadingOwners),
        requestsByMeeting: setHighlightRequest(
          state,
          action.meetingUuid,
          orderBy,
          request
        ),
        latestEntityRequestIdByMeeting: {
          ...state.latestEntityRequestIdByMeeting,
          [action.meetingUuid]: action.requestId
        },
        selectedOrderByMeeting: isOrderOwning
          ? {
              ...state.selectedOrderByMeeting,
              [action.meetingUuid]: orderBy
            }
          : state.selectedOrderByMeeting,
        lastBackgroundAttemptByHostInstance,
        snippetsLoadingRequestsById: loadingOwners
      };
    }
    case HighlightActions.FETCH_HIGHLIGHTS_SUCCESS: {
      if (!action.requestId || !action.meetingUuid) {
        return {
          ...state,
          data: {
            ...state.data,
            ...(action.meetingUuid
              ? { [action.meetingUuid]: action.highlights || [] }
              : {})
          },
          fetching: false
        };
      }

      const orderBy = getRequestOrder(action);
      const request = state.requestsByMeeting?.[action.meetingUuid]?.[orderBy];
      const loadingOwners = removeSnippetsLoadingOwner(state, action.requestId);
      if (!request || request.requestId !== action.requestId) {
        return {
          ...state,
          snippetsLoadingRequestsById: loadingOwners,
          fetching: getFetchingFromOwners(loadingOwners)
        };
      }

      const latestRequestMatches =
        state.latestEntityRequestIdByMeeting?.[action.meetingUuid] ===
        action.requestId;
      const currentRevision =
        state.entityRevisionByMeeting?.[action.meetingUuid] || 0;
      const responseStartRevision =
        action.entityRevisionAtStart ?? request.entityRevisionAtStart;
      const revisionMatches = currentRevision === responseStartRevision;
      const selectedOrder = state.selectedOrderByMeeting?.[action.meetingUuid];
      const dataOrder = state.dataOrderByMeeting?.[action.meetingUuid];
      const orderMismatch =
        !isOrderOwningRequest(action) &&
        Boolean(selectedOrder) &&
        dataOrder === selectedOrder &&
        selectedOrder !== orderBy;
      const superseded =
        action.superseded === true || !latestRequestMatches || orderMismatch;
      const stale = !superseded && (action.stale === true || !revisionMatches);
      const shouldApplyEntities =
        action.applyEntities !== false && !superseded && !stale;
      const terminalRequest: IMarkerRequestMetadata = {
        ...request,
        status: stale ? 'stale' : 'succeeded',
        error: null,
        attempt: getRequestAttempt(action),
        superseded,
        needsRevalidation: stale || action.needsRevalidation === true,
        requestedExpectedCount:
          action.requestedExpectedCount ??
          action.expectedCount ??
          request.requestedExpectedCount,
        lastSucceededAt: stale ? request.lastSucceededAt : Date.now()
      };

      return {
        ...state,
        data: shouldApplyEntities
          ? {
              ...state.data,
              [action.meetingUuid]: action.highlights || []
            }
          : state.data,
        dataOrderByMeeting: shouldApplyEntities
          ? {
              ...state.dataOrderByMeeting,
              [action.meetingUuid]: orderBy
            }
          : state.dataOrderByMeeting,
        requestsByMeeting: setHighlightRequest(
          state,
          action.meetingUuid,
          orderBy,
          terminalRequest
        ),
        snippetsLoadingRequestsById: loadingOwners,
        fetching: getFetchingFromOwners(loadingOwners)
      };
    }
    case HighlightActions.FETCH_HIGHLIGHTS_FAILURE: {
      if (!action.requestId || !action.meetingUuid) {
        return {
          ...state,
          fetching: false,
          error: action.error
        };
      }

      const orderBy = getRequestOrder(action);
      const request = state.requestsByMeeting?.[action.meetingUuid]?.[orderBy];
      const loadingOwners = removeSnippetsLoadingOwner(state, action.requestId);
      if (!request || request.requestId !== action.requestId) {
        return {
          ...state,
          snippetsLoadingRequestsById: loadingOwners,
          fetching: getFetchingFromOwners(loadingOwners)
        };
      }

      const terminalRequest: IMarkerRequestMetadata = {
        ...request,
        status: 'failed',
        error: action.error,
        attempt: getRequestAttempt(action),
        superseded: action.superseded === true,
        needsRevalidation: action.needsRevalidation === true
      };

      return {
        ...state,
        fetching: getFetchingFromOwners(loadingOwners),
        error:
          request.affectsSnippetsLoading || action.affectsSnippetsLoading
            ? action.error
            : state.error,
        requestsByMeeting: setHighlightRequest(
          state,
          action.meetingUuid,
          orderBy,
          terminalRequest
        ),
        snippetsLoadingRequestsById: loadingOwners
      };
    }
    default:
      return state;
  }
};

const fetchHighlightRecording = (
  state: IHighlightsState,
  action: IHighlightAction
): IHighlightsState => {
  switch (action.type) {
    case HighlightActions.FETCH_HIGHLIGHT_RECORDING_REQUEST:
      if (!action.uuid) return state;
      return {
        ...state,
        highlightRecordings: {
          ...(state.highlightRecordings || {}),
          [action.uuid]: {
            loading: true
          }
        }
      };
    case HighlightActions.FETCH_HIGHLIGHT_RECORDING_SUCCESS:
      if (!action.highlightRecording) return state;
      return {
        ...state,
        highlightRecordings: {
          ...(state.highlightRecordings || {}),
          [action.highlightRecording.highlight]: {
            ...action.highlightRecording,
            loading: false
          }
        }
      };
    case HighlightActions.FETCH_HIGHLIGHT_RECORDING_FAILURE:
      if (!action.uuid) return state;
      return {
        ...state,
        highlightRecordings: {
          ...(state.highlightRecordings || {}),
          [action.uuid]: {
            ...(state.highlightRecordings || {})[action.uuid],
            loading: false
          }
        }
      };
    default:
      return state;
  }
};

const highlights = (
  state: IHighlightsState = highlightsInitialState,
  action: IHighlightAction = { type: '@@INIT' }
): IHighlightsState => {
  switch (action.type) {
    // fetch members
    case HighlightActions.CREATE_HIGHLIGHT_REQUEST:
    case HighlightActions.CREATE_HIGHLIGHT_SUCCESS:
    case HighlightActions.CREATE_HIGHLIGHT_FAILURE:
      return creatingHighlight(state, action);
    case HighlightActions.UPDATE_HIGHLIGHT_REQUEST:
    case HighlightActions.UPDATE_HIGHLIGHT_SUCCESS:
    case HighlightActions.UPDATE_HIGHLIGHT_FAILURE:
    case HighlightActions.UPDATE_HIGHLIGHT_PRIVACY_REQUEST:
    case HighlightActions.UPDATE_HIGHLIGHT_PRIVACY_SUCCESS:
    case HighlightActions.UPDATE_HIGHLIGHT_PRIVACY_FAILURE:
    case HighlightActions.UPDATE_HIGHLIGHTS_SUCCESS:
    case HighlightActions.ADD_HIGHLIGHT_TO_PLAYLIST_REQUEST:
    case HighlightActions.ADD_HIGHLIGHT_TO_PLAYLIST_SUCCESS:
    case HighlightActions.ADD_HIGHLIGHT_TO_PLAYLIST_FAILURE:
    case HighlightActions.REMOVE_HIGHLIGHT_FROM_PLAYLIST_REQUEST:
    case HighlightActions.REMOVE_HIGHLIGHT_FROM_PLAYLIST_SUCCESS:
    case HighlightActions.REMOVE_HIGHLIGHT_FROM_PLAYLIST_FAILURE:
      return updatingHighlight(state, action);
    // fetch members
    case HighlightActions.DELETE_HIGHLIGHT_REQUEST:
    case HighlightActions.DELETE_HIGHLIGHT_SUCCESS:
    case HighlightActions.DELETE_HIGHLIGHT_FAILURE:
      return deletingHighlight(state, action);
    case HighlightActions.FETCH_HIGHLIGHTS_REQUEST:
    case HighlightActions.FETCH_HIGHLIGHTS_SUCCESS:
    case HighlightActions.FETCH_HIGHLIGHTS_FAILURE:
      return fetchHighlights(state, action);
    case HighlightActions.FETCH_HIGHLIGHT_CATEGORY_OVERRIDES:
      return {
        ...state,
        fetchingCategoryOverrides: true
      };
    case HighlightActions.FETCH_HIGHLIGHT_CATEGORY_OVERRIDES_SUCCESS: {
      const highlightCategoryOverrides =
        action.highlightCategoryOverrides || [];
      const highlightCatForUser = map(
        highlightCategoryOverrides,
        highlightCategoryOverride => ({
          ...highlightCategoryOverride.category,
          ...omit(highlightCategoryOverride, ['category']),
          visible: highlightCategoryOverride.visible
        })
      );

      return {
        ...state,
        highlightCategoryOverrides,
        highlightCategoriesForUser: highlightCatForUser,
        highlightCategoriesUserMap: arrToMap(highlightCatForUser),
        fetchingCategoryOverrides: false
      };
    }
    case HighlightActions.FETCH_HIGHLIGHT_CATEGORY_OVERRIDES_FAILURE:
      return {
        ...state,
        fetchingCategoryOverrides: false
      };
    case HighlightActions.HIDE_HIGHLIGHT_CATEGORY_REQUEST:
    case HighlightActions.HIDE_HIGHLIGHT_CATEGORY_SUCCESS:
    case HighlightActions.SHOW_HIGHLIGHT_CATEGORY_FAILURE: {
      const updatedOverrides = map(
        state.highlightCategoryOverrides,
        highlightCategoryOverride => {
          if (highlightCategoryOverride.uuid === action.uuid) {
            return { ...highlightCategoryOverride, visible: false };
          }
          return highlightCategoryOverride;
        }
      );
      const highlightCatForUser = map(
        updatedOverrides,
        highlightCategoryOverride => ({
          ...highlightCategoryOverride.category,
          visible: highlightCategoryOverride.visible
        })
      );
      return {
        ...state,
        highlightCategoryOverrides: updatedOverrides,
        highlightCategoriesForUser: highlightCatForUser,
        highlightCategoriesUserMap: arrToMap(highlightCatForUser)
      };
    }
    case HighlightActions.HIDE_HIGHLIGHT_CATEGORY_FAILURE:
    case HighlightActions.SHOW_HIGHLIGHT_CATEGORY_REQUEST:
    case HighlightActions.SHOW_HIGHLIGHT_CATEGORY_SUCCESS: {
      const updatedOverrides = map(
        state.highlightCategoryOverrides,
        highlightCategoryOverride => {
          if (highlightCategoryOverride.uuid === action.uuid) {
            return { ...highlightCategoryOverride, visible: true };
          }
          return highlightCategoryOverride;
        }
      );
      const highlightCatForUser = map(
        updatedOverrides,
        highlightCategoryOverride => ({
          ...highlightCategoryOverride.category,
          visible: highlightCategoryOverride.visible
        })
      );
      return {
        ...state,
        highlightCategoryOverrides: updatedOverrides,
        highlightCategoriesForUser: highlightCatForUser,
        highlightCategoriesUserMap: arrToMap(highlightCatForUser)
      };
    }
    case HighlightActions.FETCH_HIGHLIGHT_RECORDING_REQUEST:
    case HighlightActions.FETCH_HIGHLIGHT_RECORDING_SUCCESS:
    case HighlightActions.FETCH_HIGHLIGHT_RECORDING_FAILURE:
      return fetchHighlightRecording(state, action);

    case HighlightActions.CREATE_HIGHLIGHT_SHARES_REQUEST:
      return {
        ...state,
        shares: {
          ...state.shares,
          loading: true
        }
      };

    case HighlightActions.CREATE_HIGHLIGHT_SHARES_SUCCESS: {
      const { response, step, shareMethod, highlightUuids } = action;
      return {
        ...state,
        shares: {
          ...state.shares,
          loading: false,
          data: response || null,
          step,
          shareMethod,
          highlightUuids
        }
      };
    }

    case HighlightActions.CREATE_HIGHLIGHT_SHARES_ERROR:
      return {
        ...state,
        shares: {
          ...state.shares,
          loading: false,
          error: true
        }
      };

    case HighlightActions.REMOVE_HIGHLIGHT_SHARES:
      return {
        ...state,
        shares: {
          loading: false,
          error: true,
          data: null
        },
        shareHighlight: {
          loading: false,
          error: false
        }
      };

    case HighlightActions.SHARE_HIGHLIGHT_REQUEST: {
      return {
        ...state,
        shareHighlight: {
          loading: true
        }
      };
    }

    case HighlightActions.SHARE_HIGHLIGHT_SUCCESS: {
      const { shares, meetingUuid, highlightUuid, highlightObj } = action;
      if (!meetingUuid || !highlightUuid) return state;

      const meetingHighlights = state.data[meetingUuid];

      const updatedHighlights =
        meetingHighlights?.map(highlight => {
          if (highlight.uuid === highlightUuid) {
            return {
              ...highlight,
              share: shares
            };
          }
          return highlight;
        }) || [];

      if (isEmpty(updatedHighlights) && highlightObj) {
        updatedHighlights.push(highlightObj);
      }

      return {
        ...state,
        data: {
          ...state.data,
          [meetingUuid]: updatedHighlights
        },
        shares: {
          ...state.shares,
          data: shares || null
        },
        shareHighlight: {
          loading: false
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [meetingUuid]: getNextEntityRevision(state, meetingUuid)
        }
      };
    }

    case HighlightActions.SHARE_HIGHLIGHT_ERROR: {
      return {
        ...state,
        shareHighlight: {
          loading: false,
          error: true
        }
      };
    }

    case HighlightActions.UNSHARE_HIGHLIGHT_REQUEST:
      return {
        ...state,
        unshareHighlight: {
          loading: true
        }
      };

    case HighlightActions.UNSHARE_HIGHLIGHT_SUCCESS: {
      const { shares, meetingUuid, highlightUuid, highlightObj } = action;
      if (!meetingUuid || !highlightUuid) return state;

      const meetingHighlights = state.data[meetingUuid];
      const updatedHighlights =
        meetingHighlights?.map(highlight => {
          if (highlight.uuid === highlightUuid) {
            return {
              ...highlight,
              share: shares
            };
          }
          return highlight;
        }) || [];

      if (isEmpty(updatedHighlights) && highlightObj) {
        updatedHighlights.push(highlightObj);
      }

      return {
        ...state,
        data: {
          ...state.data,
          [meetingUuid]: updatedHighlights
        },
        shares: {
          ...state.shares,
          data: shares || null
        },
        unshareHighlight: {
          loading: false
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [meetingUuid]: getNextEntityRevision(state, meetingUuid)
        }
      };
    }
    case HighlightActions.UNSHARE_HIGHLIGHT_ERROR:
      return {
        ...state,
        unshareHighlight: {
          loading: false,
          error: true
        }
      };

    case HighlightActions.UPDATE_HIGHLIGHT_SHARES_REQUEST:
      return {
        ...state,
        updateHighlightShares: {
          loading: true
        }
      };

    case HighlightActions.UPDATE_HIGHLIGHT_SHARES_SUCCESS: {
      const { response } = action;
      return {
        ...state,
        updateHighlightShares: {
          loading: false
        },
        shares: {
          ...state.shares,
          data: {
            ...state.shares.data,
            ...response
          }
        }
      };
    }

    case HighlightActions.UPDATE_HIGHLIGHT_SHARES_ERROR:
      return {
        ...state,
        updateHighlightShares: {
          loading: false,
          error: true
        }
      };
    case HighlightActions.DOWNLOAD_HIGHLIGHT_REQUEST:
      return {
        ...state,
        downloadHighlight: {
          loading: true,
          error: false
        }
      };
    case HighlightActions.DOWNLOAD_HIGHLIGHT_SUCCESS: {
      return {
        ...state,
        downloadHighlight: {
          loading: false,
          error: false
        }
      };
    }
    case HighlightActions.DOWNLOAD_HIGHLIGHT_FAILURE:
      return {
        ...state,
        downloadHighlight: {
          loading: false,
          error: true
        }
      };
    default:
      return state;
  }
};

export default highlights;
